import { create } from "zustand";

/**
 * App-wide UI scale — Small / Normal / Large — the "Zoom level" / "Base font
 * size" setting Discord, Obsidian and VS Code ship. It scales EVERYTHING
 * (text, icons, spacing, controls) together by putting `zoom` on <html>
 * (see globals.css), which is what those apps do on the web too and what
 * the standardized CSS `zoom` (Chrome 128+, Firefox 126+, Safari) was
 * specified for: `100vh` and `position: fixed` still track the real
 * viewport, `getBoundingClientRect` and hit-testing agree, only px lengths
 * scale (measured in Chrome 151, 2026-08-19).
 *
 * The factors follow the display-scaling ladder every user already knows
 * from Windows (100 / 125 / 150 %) and browser zoom (100 / 125 / 150 %):
 *   small  = 100 %  — the compact layout the app shipped with (14px body);
 *                    THE DEFAULT again since 2026-09-06 — the owner ran
 *                    Normal for two weeks and found "the fonts and icons
 *                    too huge"; the setting stays, the default moved back
 *   normal = 125 %  — 14px body → 17.5px, 16px icons → 20px (the default
 *                    from 2026-08-19 to 2026-09-06)
 *   large  = 150 %  — high-resolution displays, reading at a distance
 *
 * Per-device preference (like the theme): persisted in localStorage, never
 * on the server — a laptop and a wall monitor want different answers.
 * Applied at import time so the first paint is already at scale.
 *
 * PERSISTED ONLY ON AN EXPLICIT PICK (2026-09-06). The first cut wrote the
 * key on EVERY apply, including the import-time apply of the DEFAULT — so
 * for the two weeks Normal was the default, every device that merely opened
 * the app got "normal" stamped as if chosen, and moving the default back to
 * Small could not reach it (the owner: "fonts and icons are still huge",
 * on a build whose default was already 100 %). Now `setScale` writes the
 * value AND a "chosen" marker; the load path treats an unmarked value that
 * equals a former or current default as a stamp and forgets it, and keeps
 * (and marks) any other unmarked value, which can only have been a pick.
 */
export type UiScale = "small" | "normal" | "large";

export const UI_SCALES: readonly UiScale[] = ["small", "normal", "large"] as const;

export const UI_SCALE_FACTORS: Readonly<Record<UiScale, number>> = {
	small: 1,
	normal: 1.25,
	large: 1.5,
};

export const DEFAULT_UI_SCALE: UiScale = "small";

export const UI_SCALE_STORAGE_KEY = "agnt-ui-scale";
// Set beside the value by an explicit pick only. Absent ⇒ the value was
// written by a build that persisted the default at import time.
export const UI_SCALE_CHOSEN_KEY = "agnt-ui-scale-chosen";
// Every default this store has ever stamped at import time without a pick:
// "normal" (2026-08-19 → 2026-09-06) and "small" (the default since). An
// unmarked stored value in this set is a stamp, not a choice.
const STAMPED_DEFAULTS: readonly UiScale[] = ["normal", "small"] as const;
const STORAGE_KEY = UI_SCALE_STORAGE_KEY;

function isUiScale(value: unknown): value is UiScale {
	return typeof value === "string" && (UI_SCALES as readonly string[]).includes(value);
}

function getInitialScale(): UiScale {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (isUiScale(stored)) {
			if (localStorage.getItem(UI_SCALE_CHOSEN_KEY) === "1") return stored;
			if (STAMPED_DEFAULTS.includes(stored)) {
				// Written by an earlier build's import-time apply, never picked:
				// forget it so the current default applies on this device.
				localStorage.removeItem(STORAGE_KEY);
				return DEFAULT_UI_SCALE;
			}
			// Never a default ⇒ an explicit pick from before the marker existed.
			localStorage.setItem(UI_SCALE_CHOSEN_KEY, "1");
			return stored;
		}
	} catch {
		/* storage unavailable (privacy mode) — fall through to the default */
	}
	return DEFAULT_UI_SCALE;
}

function applyScale(scale: UiScale) {
	const root = document.documentElement;
	const factor = UI_SCALE_FACTORS[scale];
	root.setAttribute("data-ui-scale", scale);
	// Desktop (Electron): TRUE page zoom via the preload bridge — like
	// Ctrl+=, so media queries respond, devicePixelRatio updates, and
	// pointer math has no CSS-px/viewport-px split. The CSS variable is
	// pinned to 1 so `html { zoom }`, every `calc(… / var(--ui-scale))`
	// divisor and `.ui-scale-reset` all become no-ops — double-zooming
	// through both mechanisms is the failure mode this branch exists to
	// prevent. Browser: CSS zoom on <html> via the variable, as before.
	const desktopZoom = window.agntspaceDesktop?.setZoomFactor;
	if (desktopZoom) {
		desktopZoom(factor);
		root.style.setProperty("--ui-scale", "1");
	} else {
		root.style.setProperty("--ui-scale", String(factor));
	}
}

/** Only an explicit pick is remembered — never the import-time apply. */
function persistChoice(scale: UiScale) {
	try {
		localStorage.setItem(STORAGE_KEY, scale);
		localStorage.setItem(UI_SCALE_CHOSEN_KEY, "1");
	} catch {
		/* storage unavailable — the scale still applies for this session */
	}
}

interface UiScaleState {
	scale: UiScale;
	setScale: (scale: UiScale) => void;
}

export const useUiScaleStore = create<UiScaleState>((set) => ({
	scale: getInitialScale(),
	setScale: (scale) => {
		applyScale(scale);
		persistChoice(scale);
		set({ scale });
	},
}));

// Apply immediately on import — before React renders — so nothing paints
// at the wrong size and then jumps.
applyScale(getInitialScale());
